import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import type { KnowledgeHealth, KnowledgeDupGroup } from "../lib/types.js";
import { Icon } from "../components/Icon.js";

/**
 * A read-only maintenance dashboard for story knowledge quality: AI-vs-user
 * counts, possible duplicates, object-like locations, orphaned entries. It only
 * surfaces issues (it never changes data), so a clean project simply shows "looks
 * healthy". Collapsed by default to stay out of the way.
 */
export function KnowledgeHealthCard({ projectId, defaultOpen = false }: { projectId: string; defaultOpen?: boolean }) {
  const [health, setHealth] = useState<KnowledgeHealth | null>(null);
  const [open, setOpen] = useState(defaultOpen);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || health) return;
    setLoading(true);
    api.knowledgeHealth(projectId)
      .then(setHealth)
      .catch((e) => setError(e instanceof Error ? e.message : "Could not load knowledge health."))
      .finally(() => setLoading(false));
  }, [open, health, projectId]);

  const dupCount = health
    ? health.characters.possibleDuplicates.length + health.locations.possibleDuplicates.length +
      health.storyBible.possibleDuplicates.length + health.timeline.possibleDuplicates.length +
      health.plotThreads.possibleDuplicates.length
    : 0;
  const issueCount = health
    ? dupCount + health.characters.placeholders.length + health.locations.objectLike.length
    : 0;

  return (
    <div className="insights-card card" style={{ marginTop: 10 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}
      >
        <Icon name={open ? "chevronDown" : "chevronRight"} size={16} />
        <strong style={{ fontSize: 13 }}>Knowledge Health</strong>
        <span className="faint" style={{ fontSize: 11.5 }}>
          {health ? (issueCount === 0 ? "Looks healthy" : `${issueCount} thing${issueCount === 1 ? "" : "s"} to review`) : "Maintenance dashboard"}
        </span>
      </button>

      {open && (
        <div style={{ marginTop: 10 }}>
          {loading && <p className="faint">Checking…</p>}
          {error && <p className="faint" style={{ color: "var(--danger,#c0392b)" }}>{error}</p>}
          {health && (
            <div className="col" style={{ gap: 12 }}>
              <HealthRow label="Characters" total={health.characters.total} ai={health.characters.aiGenerated}
                issues={[
                  dupLine(health.characters.possibleDuplicates, "possible duplicate"),
                  health.characters.placeholders.length ? `${health.characters.placeholders.length} placeholder name(s): ${health.characters.placeholders.map((p) => p.name).slice(0, 5).join(", ")}` : null,
                  health.characters.neverMentioned.length ? `${health.characters.neverMentioned.length} never mentioned in prose` : null,
                  health.characters.missingSource ? `${health.characters.missingSource} AI entries without a source scene` : null
                ]} />
              <HealthRow label="Locations" total={health.locations.total} ai={health.locations.aiGenerated}
                issues={[
                  dupLine(health.locations.possibleDuplicates, "possible duplicate"),
                  health.locations.objectLike.length ? `${health.locations.objectLike.length} object-like (not places): ${health.locations.objectLike.map((l) => l.name).slice(0, 5).join(", ")}` : null,
                  health.locations.zeroMention.length ? `${health.locations.zeroMention.length} with zero mentions` : null
                ]} />
              <HealthRow label="Story Bible" total={health.storyBible.total} ai={health.storyBible.aiGenerated}
                issues={[dupLine(health.storyBible.possibleDuplicates, "possible duplicate")]} />
              <HealthRow label="Timeline" total={health.timeline.total} ai={health.timeline.aiGenerated}
                issues={[
                  dupLine(health.timeline.possibleDuplicates, "possible duplicate"),
                  health.timeline.missingSceneRef ? `${health.timeline.missingSceneRef} without a scene reference` : null
                ]} />
              <HealthRow label="Plot Threads" total={health.plotThreads.total} ai={health.plotThreads.aiGenerated}
                issues={[
                  dupLine(health.plotThreads.possibleDuplicates, "possible duplicate"),
                  health.plotThreads.unresolved ? `${health.plotThreads.unresolved} unresolved` : null,
                  health.plotThreads.noRelatedScenes ? `${health.plotThreads.noRelatedScenes} with no related scenes` : null
                ]} />
              <p className="faint" style={{ fontSize: 10.5, margin: 0 }}>
                Suggestions only — nothing here is changed automatically. Review and merge/edit in each section.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function dupLine(groups: KnowledgeDupGroup[], label: string): string | null {
  if (groups.length === 0) return null;
  const sample = groups.slice(0, 3).map((g) => g.items.map((i) => i.name).join(" ≈ ")).join("; ");
  return `${groups.length} ${label} group(s): ${sample}`;
}

function HealthRow({ label, total, ai, issues }: { label: string; total: number; ai: number; issues: (string | null)[] }) {
  const real = issues.filter((x): x is string => !!x);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <strong style={{ fontSize: 12 }}>{label}</strong>
        <span className="faint" style={{ fontSize: 11 }}>{total} total · {ai} AI · {total - ai} you</span>
      </div>
      {real.length === 0 ? (
        <div className="faint" style={{ fontSize: 11.5, color: "var(--success,#2e7d32)" }}>No issues</div>
      ) : (
        <ul style={{ margin: "2px 0 0", paddingLeft: 18, fontSize: 11.5 }}>
          {real.map((x, i) => <li key={i} style={{ marginBottom: 2 }}>{x}</li>)}
        </ul>
      )}
    </div>
  );
}
